import { logSwallowed } from '@/lib/errors';
// ============================================================================
// pwaInstall.ts — ЕДИНЫЙ механизм установки PWA (SRP/SSoT).
// Кнопка/оффер установки показываются ПО КРИТЕРИЯМ (web, не standalone, не Wails,
// не установлено). После установки — скрываются НАВСЕГДА (флаг localStorage).
// Уведомления НЕ отсюда: единая точка включения — после авторизации.
//
// ДИАГНОСТИКА: каждое событие (beforeinstallprompt/appinstalled/критерии/состояние
// SW) ЛОГГИРУЕТСЯ в журнал регистрации (eventLog, SSoT) — без гаданий видно,
// почему Chrome не предлагает установку.
// ============================================================================

import { create } from 'zustand';
import { isWailsMode } from '@/lib/wailsBridge';
import { opfsClear } from '@/lib/storage/opfs';

const INSTALLED_FLAG = 'hydra_pwa_installed';
const logId = 'pwaInstall';

// Журнал регистрации (асинхронно, не блокирует). Категория ui.
function diag(message: string, details?: string): void {
  try {
    void import('@/lib/eventLog').then((m) => {
      m.eventLog.info('ui', `[${logId}] ${message}`, details ? { details } : undefined).catch((e) => logSwallowed(e, 'lib/pwaInstall.ts:23'));
    });
  } catch { /* ignore */ }
}

interface PwaInstallState {
  deferredPrompt: any | null;
  /** Браузер прислал beforeinstallprompt — установка в один клик */
  installable: boolean;
  /** Приложение УСТАНОВЛЕНО (standalone/display-mode или appinstalled). */
  isInstalled: boolean;
  init: () => void;
  /** Запустить установку: 'prompt' — диалог показан, 'manual' — события нет
   * (iOS/Chrome не прислали beforeinstallprompt — показать инструкцию). */
  requestInstall: () => Promise<'prompt' | 'manual'>;
  clear: () => void;
}

function markInstalled(): void {
  try { localStorage.setItem(INSTALLED_FLAG, '1'); } catch { /* ignore */ }
}

export const usePwaInstallStore = create<PwaInstallState>((set, get) => ({
  deferredPrompt: null,
  installable: false,
  isInstalled: false,
  init: () => {
    if (typeof window === 'undefined') return;
    // Уже установлено (standalone/display-mode или флаг appinstalled) —
    // кнопка/оффер не показываются (isInstalled). Проверка сразу при старте.
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as any).standalone === true;
    const installed = isStandalone || (() => { try { return localStorage.getItem(INSTALLED_FLAG) === '1'; } catch { return false; } })();
    diag('init: слушаем beforeinstallprompt/appinstalled',
      `secure=${String(window.isSecureContext)} wails=${String(isWailsMode())} standalone=${String(isStandalone)} installed=${String(installed)} ua=${navigator.userAgent.slice(0, 80)}`);
    set({ isInstalled: installed });
    const onPrompt = (e: Event) => {
      e.preventDefault();
      diag('beforeinstallprompt ПРИШЁЛ — приложение installable, кнопка доступна');
      set({ deferredPrompt: e, installable: true });
    };
    const onInstalled = () => {
      diag('appinstalled — приложение установлено, скрываем оффер/кнопку');
      markInstalled();
      set({ deferredPrompt: null, installable: false, isInstalled: true });
    };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // ПРИЛОЖЕНИЕ ПЕРЕШЛО В STANDALONE (установлено Chrome и открыто отдельным
    // окном): исходная вкладка остаётся живой, но display-mode меняется →
    // НАДЁЖНО скрываем кнопку «Установить» (appinstalled мог не сработать,
    // если установка прошла через prompt()/другую вкладку).
    const mql = window.matchMedia('(display-mode: standalone)');
    const onDisplayMode = (e: MediaQueryListEvent) => {
      if (e.matches) {
        diag('display-mode: standalone — приложение установлено, скрываем кнопку');
        markInstalled();
        set({ deferredPrompt: null, installable: false, isInstalled: true });
      }
    };
    if (mql.addEventListener) mql.addEventListener('change', onDisplayMode);

    // Диагностика: состояние Service Worker (Chrome требует активный SW для installability)
    setTimeout(() => {
      if (!('serviceWorker' in navigator)) { diag('SW: navigator.serviceWorker НЕТ — PWA не установимо'); return; }
      navigator.serviceWorker.getRegistration().then((reg) => {
        diag('SW: регистрация', reg
          ? `scope=${reg.scope} active=${reg.active ? 'ДА' : 'нет'} waiting=${reg.waiting ? 'ДА' : 'нет'} controller=${navigator.serviceWorker.controller ? 'ДА' : 'нет'}`
          : 'НЕТ (SW не зарегистрирован — Chrome НЕ предложит установку!)');
      }).catch((err: any) => diag('SW: ошибка проверки', String(err?.message || err)));
    }, 1500);
  },
  requestInstall: async () => {
    const p = get().deferredPrompt;
    if (p) {
      try {
        diag('requestInstall: вызываем prompt() — браузерный диалог установки');
        p.prompt();
        const choice = await p.userChoice;
        diag('requestInstall: userChoice=' + String(choice?.outcome || 'unknown'));
        if (choice?.outcome === 'accepted') {
          markInstalled();
          set({ deferredPrompt: null, installable: false, isInstalled: true });
        }
      } catch (e: any) {
        diag('requestInstall: prompt() кинул ошибку', String(e?.message || e));
        return 'manual';
      }
      return 'prompt';
    }
    // beforeinstallprompt ещё не пришёл (SW мог не успеть активироваться на
    // первом визите). Подталкиваем SW и ЖДЁМ событие до 3 секунд — если
    // приложение становится installable прямо сейчас, показываем НАСТОЯЩИЙ
    // диалог установки, а не инструкцию.
    diag('requestInstall: deferredPrompt null — будим SW и ждём beforeinstallprompt до 3с');
    try {
      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          await reg.update().catch((e) => { logSwallowed(e, 'lib/pwaInstall.ts:125'); return undefined; });
        } else {
          await navigator.serviceWorker.register('/sw.js').catch((e) => { logSwallowed(e, 'lib/pwaInstall.ts:127'); return undefined; });
        }
      }
    } catch { /* SW недоступен — идём в manual */ }
    const deadline = Date.now() + 3000;
    while (Date.now() < deadline) {
      const p2 = get().deferredPrompt;
      if (p2) {
        diag('requestInstall: beforeinstallprompt пришёл после ожидания — prompt()');
        try {
          p2.prompt();
          const choice = await p2.userChoice;
          diag('requestInstall: userChoice=' + String(choice?.outcome || 'unknown'));
          if (choice?.outcome === 'accepted') {
            markInstalled();
            set({ deferredPrompt: null, installable: false, isInstalled: true });
          }
        } catch (e: any) {
          diag('requestInstall: prompt() кинул ошибку после ожидания', String(e?.message || e));
          return 'manual';
        }
        return 'prompt';
      }
      await new Promise((r) => setTimeout(r, 300));
    }
    diag('requestInstall: beforeinstallprompt так и не пришёл за 3с — показать инструкцию');
    return 'manual';
  },
  clear: () => set({ deferredPrompt: null, installable: false }),
}));

/** Приложение УСТАНОВЛЕНО: display-mode standalone ИЛИ флаг (appinstalled). */
export function isPwaInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as any).standalone === true;
  if (standalone) return true;
  try { return localStorage.getItem(INSTALLED_FLAG) === '1'; } catch { return false; }
}

/** Можно ли предлагать установку: web (не Wails), не standalone, не установлено. */
export function canOfferPwaInstall(): boolean {
  if (typeof window === 'undefined') return false;
  if (isWailsMode()) return false;
  if (isPwaInstalled()) return false;
  return true;
}

// ============================================================================
// reinstallPwa — «ПЕРЕУСТАНОВИТЬ ПРИЛОЖЕНИЕ» / полная очистка оболочки PWA.
// Установленное PWA нельзя удалить программно (нет API), но можно сделать
// «обнуление всего» (как удалить вручную): очистить IndexedDB + localStorage
// + SW-кэш + unregister SW → при следующей загрузке Chrome снова считает
// приложение installable → beforeinstallprompt → «Установить».
// ДАННЫЕ НЕ ТЕРЯЮТСЯ: SSoT — сервер; при входе всё подтянется заново.
// ============================================================================
export async function reinstallPwa(): Promise<void> {
  if (typeof window === 'undefined' || isWailsMode()) return;
  diag('reinstallPwa: полный сброс PWA (IndexedDB + localStorage + SW-кэш + unregister)');
  // 1. Сбросить флаг «установлено» (кнопка снова активна)
  try { localStorage.removeItem(INSTALLED_FLAG); } catch { /* ignore */ }
  usePwaInstallStore.setState({ deferredPrompt: null, installable: false, isInstalled: false });
  // 2. Удалить ВСЕ SW-кэши
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch((e) => { logSwallowed(e, 'lib/pwaInstall.ts:194'); return undefined; })));
    }
  } catch { /* ignore */ }
  // 3. Деактивировать Service Worker
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch((e) => { logSwallowed(e, 'lib/pwaInstall.ts:201'); return undefined; })));
    }
  } catch { /* ignore */ }
  // 4. ОЧИСТИТЬ IndexedDB (кэш чтения, sync-очередь, сессия офлайна) — как
  //    при удалении приложения. Серверные данные не теряются (SSoT — сервер).
  try {
    if ('indexedDB' in window) {
      const dbs = await indexedDB.databases().catch(() => [] as IDBDatabaseInfo[]);
      await Promise.all(dbs.map((db) => db.name ? new Promise<void>((res) => {
        const req = indexedDB.deleteDatabase(db.name as string);
        req.onsuccess = () => res();
        req.onerror = () => res();
        req.onblocked = () => res();
      }) : Promise.resolve()));
    }
  } catch { /* ignore */ }
  // 4b. ОЧИСТИТЬ OPFS (крупные блобы медиа/ассетов).
  await opfsClear().catch((e) => { logSwallowed(e, 'lib/pwaInstall.ts:218'); return undefined; });
  // 5. ОЧИСТИТЬ localStorage приложения (сессия/настройки — сброс, как после
  //    удаления приложения; при входе всё восстановится с сервера).
  try {
    const keys = Object.keys(localStorage);
    for (const k of keys) {
      // Сохраняем ТОЛЬКО явные «внешние» (ярлыки серверов/последний логин) —
      // остальное чистим полностью (имитация удаления приложения).
      if (k.startsWith('hydra_') || k.startsWith('zdc_')) {
        try { localStorage.removeItem(k); } catch { /* ignore */ }
      }
    }
  } catch { /* ignore */ }
  diag('reinstallPwa: IndexedDB + localStorage + SW-кэш очищены, SW unregistered — перезагрузка');
  window.location.reload();
}
